import { PracticeQueryParams } from 'constants/application-constants'

import { lazy, Suspense, useEffect, useMemo } from 'react'

import { LoadingIndicator, Typography } from 'components/common'
import { useParams, useSearchParams } from 'react-router-dom'
import { DocumentEntity } from 'redux-store/models'

import styles from './DocumentInformation.module.scss'
import { createFileData } from './DocumentInformation.utils'
import NoDocCard from './NoDocCard'
import PdfStatusEnum from '../../../../../constants/enums/pdfStatus'
import { useFrontOfficeCheck, useIsBelowBreakpoint } from '../../../../../hooks'
import { documentApi } from '../../api/documentsApi'
import { DocDetailQuery } from '../../enums'
import DocRevisionEnum, { DocRevisions } from '../../enums/DocRevision'
import {
  getPreferredStatus,
  isValidStatus,
} from '../DocumentDetailsLayout/utils'

const DocumentAudio = lazy(
  () => import('../DocumentDisplay/DocumentAudio/DocumentAudio')
)
const DocumentImage = lazy(
  () => import('../DocumentDisplay/DocumentImage/DocumentImage')
)
const DocumentPdf = lazy(
  () => import('../DocumentDisplay/DocumentPdf/DocumentPdf')
)
const DocumentUnviewable = lazy(
  () => import('../DocumentDisplay/DocumentUnviewable/DocumentUnviewable')
)

const DocumentSideBar = lazy(() => import('../DocumentSideBar/DocumentSideBar'))
const DocumentVideo = lazy(
  () => import('../DocumentDisplay/DocumentVideo/DocumentVideo')
)

const DocumentInformation = () => {
  const { documentId } = useParams()
  const [searchParams] = useSearchParams()
  const revisionStatus = searchParams.get(PracticeQueryParams.REVISION_STATUS)
  const practiceId = searchParams.get(DocDetailQuery.PRACTICE_ID)
  const isTabLand = useIsBelowBreakpoint('tabLand')
  const isFrontOffice = useFrontOfficeCheck()

  const httpGetDocument = documentApi.useGetDocumentByIdQuery(
    documentId ?? '',
    {
      skip: !documentId,
    }
  )

  const getDocTitle = (httpGetDoc: any, revisionStatus: DocRevisions) => {
    if (revisionStatus === DocRevisionEnum.draft)
      return httpGetDoc?.data?.data.latest_draft_version?.metadata[0]?.title
    if (revisionStatus === DocRevisionEnum.approval)
      return httpGetDoc?.data?.data.current_approvalish_version?.metadata[0]
        ?.title
    return httpGetDoc?.data?.data.metadata?.title
  }

  const docTitle = getDocTitle(
    httpGetDocument,
    revisionStatus as DocRevisionEnum
  )

  const displayedFile = useMemo(() => {
    if (!httpGetDocument.isSuccess) return null

    const file = getCurrentDisplayedFile(
      revisionStatus,
      httpGetDocument.data.data
    )

    if (!file) return null

    return createFileData(file)
  }, [httpGetDocument.data, httpGetDocument.isSuccess, revisionStatus])

  useEffect(() => {
    if (
      displayedFile?.type === 'pdf' &&
      displayedFile?.pdf?.progress?.status === PdfStatusEnum.IN_PROGRESS
    ) {
      const timeoutId = setTimeout(() => {
        httpGetDocument.refetch()
      }, 30000)

      return () => clearTimeout(timeoutId)
    }
  }, [displayedFile, httpGetDocument])

  return (
    <div className={styles.root}>
      {!httpGetDocument.isFetching &&
        !!displayedFile &&
        isTabLand &&
        !isFrontOffice && (
          <div className={styles.title}>
            <Typography type="text" size="large" fontWeight="bold" element="h1">
              {docTitle}
            </Typography>
          </div>
        )}
      <div className={styles.main}>
        {!httpGetDocument.isFetching && !!displayedFile && (
          <>
            {displayedFile.type === 'image' && (
              <Suspense fallback={<LoadingIndicator />}>
                <DocumentImage file={displayedFile} />
              </Suspense>
            )}
            {displayedFile.type === 'video' && (
              <Suspense fallback={<LoadingIndicator />}>
                <DocumentVideo file={displayedFile} />
              </Suspense>
            )}
            {displayedFile.type === 'pdf' && (
              <Suspense fallback={<LoadingIndicator />}>
                <DocumentPdf file={displayedFile} />
              </Suspense>
            )}
            {displayedFile.type === 'audio' && (
              <Suspense fallback={<LoadingIndicator />}>
                <DocumentAudio file={displayedFile} />
              </Suspense>
            )}
            {displayedFile.type === 'unviewable' && (
              <Suspense fallback={<LoadingIndicator />}>
                <DocumentUnviewable file={displayedFile} />
              </Suspense>
            )}
          </>
        )}
        {httpGetDocument.isSuccess && !displayedFile && (
          <div className={styles.noData}>
            <NoDocCard
              practiceId={practiceId}
              revisionStatus={revisionStatus}
            />
          </div>
        )}
        {httpGetDocument.isFetching && <LoadingIndicator />}
      </div>
      {!!displayedFile?.type && <DocumentSideBar />}
    </div>
  )
}

export default DocumentInformation

const getCurrentDisplayedFile = (
  revisionStatus: string | null,
  docData: DocumentEntity
) => {
  // TODO: this logic should be moved to back-end (will require DTO restructure)

  let status = revisionStatus

  if (!isValidStatus(status)) {
    status = getPreferredStatus([])
  }

  if (status === DocRevisionEnum.draft) {
    return docData.latest_draft_version?.files[0]
  }
  if (status === DocRevisionEnum.approval) {
    return docData.current_approvalish_version?.files[0]
  }
  if (status === DocRevisionEnum.published) {
    return docData.latest_approved_version?.files[0]
  }

  return null
}
